import { useEffect, useState } from 'react';
import { getAuthHeaders, getImpersonationIdentityKey, makeApiCall } from '../utils/apiConfig';
import { useAuth } from '../contexts/AuthContext';

/**
 * Whether a product's resolved price differs across the store's locations,
 * batched across every card asking at once instead of one request per row.
 * Backed by `GET /store/pricing/breakdown/by-location?productIds=...` (up to
 * 25 per call). Requests made within a short window are grouped into one
 * call; results are cached for the life of the page.
 *
 * The cache and in-flight batches are keyed by productId *and* by an acting
 * identity: the signed-in user's uid plus any impersonation target. Without
 * that, a value fetched for one store stays cached across an organization
 * switch or an admin entering/leaving impersonation, so the same catalog
 * product would keep showing another store's "varies by location" answer.
 */

type Listener = (varies: boolean | null) => void;

// Keyed by `${identityKey}:${productId}` so a switch of acting identity
// never serves a stale answer cached under the previous one.
const pending = new Set<string>();
const listeners = new Map<string, Set<Listener>>();
const cache = new Map<string, boolean>();
// One timer per acting identity, so a switch mid-batch-window never drops
// the other identity's request onto the floor.
const timers = new Map<string, ReturnType<typeof setTimeout>>();

function cacheKey(identityKey: string, productId: string): string {
  return `${identityKey}:${productId}`;
}

function schedule(identityKey: string) {
  if (timers.has(identityKey)) return;
  timers.set(
    identityKey,
    setTimeout(() => {
      timers.delete(identityKey);
      void flush(identityKey);
    }, 40)
  );
}

async function flush(identityKey: string) {
  const ids = Array.from(pending)
    .filter((key) => key.startsWith(`${identityKey}:`))
    .map((key) => key.slice(identityKey.length + 1))
    .slice(0, 25);
  ids.forEach((id) => pending.delete(cacheKey(identityKey, id)));
  if (ids.length === 0) return;

  const notify = (id: string, value: boolean | null) => {
    const key = cacheKey(identityKey, id);
    if (value !== null) cache.set(key, value);
    listeners.get(key)?.forEach((cb) => cb(value));
    listeners.delete(key);
  };

  try {
    const headers = await getAuthHeaders();
    const response = await makeApiCall(
      `/store/pricing/breakdown/by-location?productIds=${encodeURIComponent(ids.join(','))}`,
      { headers }
    );
    if (!response.ok) throw new Error('by-location request failed');
    const body = await response.json();
    const data = body?.data || {};
    for (const id of ids) {
      const entry = data[id];
      const varies =
        entry && entry.found
          ? Boolean(entry.locations?.some((l: { differsFromPrimary?: boolean }) => l.differsFromPrimary))
          : false;
      notify(id, varies);
    }
  } catch {
    ids.forEach((id) => notify(id, null));
  }

  if (Array.from(pending).some((key) => key.startsWith(`${identityKey}:`))) schedule(identityKey);
}

/** `null` while unknown or on error; otherwise whether the price varies by location. */
export function usePriceVariesByLocation(productId: string | undefined, enabled: boolean): boolean | null {
  const { currentUser } = useAuth();
  const identityKey = `${currentUser?.uid || 'anonymous'}|${getImpersonationIdentityKey()}`;
  const key = productId ? cacheKey(identityKey, productId) : undefined;

  const [varies, setVaries] = useState<boolean | null>(() => (key && cache.has(key) ? cache.get(key)! : null));

  useEffect(() => {
    if (!enabled || !productId || !key) return;
    if (cache.has(key)) {
      setVaries(cache.get(key)!);
      return;
    }
    setVaries(null);
    const cb: Listener = (v) => setVaries(v);
    if (!listeners.has(key)) listeners.set(key, new Set());
    listeners.get(key)!.add(cb);
    pending.add(key);
    schedule(identityKey);
    return () => {
      listeners.get(key)?.delete(cb);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, enabled, identityKey]);

  return varies;
}
